import { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import PropTypes from "prop-types";

import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import ArgonBadge from "components/ArgonBadge";
import ArgonAvatar from "components/ArgonAvatar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import ApnaTabs from "components/ApnaTabs/ApnaTabs";
import PaginationBar from "components/PaginationBar/PaginationBar";
import { getAllSpiffProgress, getSpiffStats, processSpiffPayout } from "services/spiffService";

const statusColor = {
  IN_PROGRESS: "info",
  EARNED: "success",
  WITHDRAW_REQUESTED: "warning",
  PAID: "success",
  DECLINED: "error",
};

function Spiff() {
  const [progressList, setProgressList] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [selectedProgress, setSelectedProgress] = useState(null);
  const [viewDialog, setViewDialog] = useState(false);
  const [processDialog, setProcessDialog] = useState(false);
  const [processStatus, setProcessStatus] = useState("");
  const [payAmount, setPayAmount] = useState("");
  const [transactionId, setTransactionId] = useState("");
  const [screenShotFile, setScreenShotFile] = useState(null);
  const [processing, setProcessing] = useState(false);

  const fetchStats = async () => {
    try {
      const response = await getSpiffStats();
      if (response.success) setStats(response.data || {});
    } catch (error) {
      console.error("SPIFF stats error:", error);
    }
  };

  const fetchProgress = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: rowsPerPage,
      };
      if (searchQuery) params.search = searchQuery;
      if (currentTab !== "All") params.status = currentTab;

      const response = await getAllSpiffProgress(params);
      if (response.success) {
        setProgressList(response.data || []);
        setPagination({
          page: response.pagination?.page || pagination.page,
          limit: rowsPerPage,
          total: response.pagination?.total || 0,
          totalPages: response.pagination?.totalPages || 1,
        });
      }
    } catch (error) {
      toast.error(error.message || "Failed to fetch SPIFF progress");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchProgress();
  }, [pagination.page, rowsPerPage, currentTab, searchQuery]);

  const openProcessDialog = (progress) => {
    setSelectedProgress(progress);
    setProcessStatus("");
    setPayAmount(progress.requestAmount || progress.amount || "");
    setTransactionId("");
    setScreenShotFile(null);
    setProcessDialog(true);
  };

  const handleProcessPayout = async () => {
    if (!selectedProgress) return;
    if (!processStatus) {
      toast.error("Please select payout status");
      return;
    }
    if (processStatus === "APPROVED" && !transactionId.trim()) {
      toast.error("Transaction ID is required");
      return;
    }
    if (processStatus === "APPROVED" && !screenShotFile) {
      toast.error("Payment screenshot is required");
      return;
    }

    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append("status", processStatus);
      formData.append("payAmount", payAmount || 0);
      if (processStatus === "APPROVED") {
        formData.append("transactionId", transactionId);
        formData.append("screenShot", screenShotFile);
      }

      const response = await processSpiffPayout(selectedProgress._id, formData);
      if (response.success) {
        toast.success("SPIFF payout processed successfully");
        setProcessDialog(false);
        fetchStats();
        fetchProgress();
      }
    } catch (error) {
      toast.error(error.message || "Failed to process SPIFF payout");
    } finally {
      setProcessing(false);
    }
  };

  const tabsData = [
    { label: "All", count: stats.total || 0, color: "#1f6feb", content: "" },
    { label: "IN_PROGRESS", count: stats.inProgress || 0, color: "#0ea5e9", content: "" },
    { label: "EARNED", count: stats.earned || 0, color: "#22c55e", content: "" },
    { label: "WITHDRAW_REQUESTED", count: stats.requested || 0, color: "#f59e0b", content: "" },
    { label: "PAID", count: stats.paid || 0, color: "#16a34a", content: "" },
    { label: "DECLINED", count: stats.declined || 0, color: "#ef4444", content: "" },
  ];

  const Author = ({ image, name, phone }) => (
    <ArgonBox display="flex" alignItems="center" px={0.5} py={0.1}>
      <ArgonBox mr={2}>
        <ArgonAvatar src={image} alt={name} size="sm" variant="full" />
      </ArgonBox>
      <ArgonBox display="flex" flexDirection="column">
        <ArgonTypography variant="button" fontWeight="medium">
          {name}
        </ArgonTypography>
        <ArgonTypography variant="caption" color="secondary">
          {phone}
        </ArgonTypography>
      </ArgonBox>
    </ArgonBox>
  );

  Author.propTypes = {
    image: PropTypes.string,
    name: PropTypes.string.isRequired,
    phone: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  };
  const columns = [
    { name: "sno", align: "center" },
    { name: "Name(Mobile)", align: "left" },
    { name: "Approval Progress", align: "center" },
    { name: "Incentive Amount", align: "center" },
    { name: "Request Date", align: "center" },
    { name: "Request Amount", align: "center" },
    { name: "Pay Amount", align: "center" },
    { name: "Pay Date", align: "center" },
    { name: "Status", align: "center" },
    { name: "Screen Shot", align: "center" },
    { name: "Transaction ID", align: "center" },
    { name: "Action", align: "center" },
  ];

  const rows = progressList.map((progress, index) => {
    const tutor = progress.tutorId || {};
    const serialNumber = (pagination.page - 1) * rowsPerPage + index + 1;

    return {
      sno: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {serialNumber}
        </ArgonTypography>
      ),
      "Name(Mobile)": (
        <Author
          image={tutor.profileImage || ""}
          name={tutor.fullName || "N/A"}
          phone={tutor.phone || tutor.email || "N/A"}
        />
      ),
      "Approval Progress": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {progress.approvalStreak || 0}/{progress.requiredApprovals || 0}
        </ArgonTypography>
      ),
      "Incentive Amount": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          Rs {progress.amount || 0}
        </ArgonTypography>
      ),
      "Request Date": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {progress.requestDate ? new Date(progress.requestDate).toLocaleDateString() : "N/A"}
        </ArgonTypography>
      ),
      "Request Amount": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          Rs {progress.requestAmount || 0}
        </ArgonTypography>
      ),
      "Pay Amount": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          Rs {progress.payAmount || 0}
        </ArgonTypography>
      ),
      "Pay Date": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {progress.payDate ? new Date(progress.payDate).toLocaleDateString() : "N/A"}
        </ArgonTypography>
      ),
      Status: (
        <ArgonBadge
          variant="gradient"
          badgeContent={progress.status || "N/A"}
          color={statusColor[progress.status] || "secondary"}
          size="xs"
          container
        />
      ),
      "Screen Shot": progress.screenShot ? (
        <ArgonTypography
          component="a"
          href={progress.screenShot}
          target="_blank"
          variant="caption"
          color="primary"
          fontWeight="medium"
        >
          View
        </ArgonTypography>
      ) : (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          N/A
        </ArgonTypography>
      ),
      "Transaction ID": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {progress.transactionId || "N/A"}
        </ArgonTypography>
      ),
      Action: (
        <ArgonBox display="flex" alignItems="center" justifyContent="center" gap={0}>
          {progress.status === "WITHDRAW_REQUESTED" && (
            <Button
              variant="text"
              size="large"
              sx={{ minWidth: "32px", p: 0 }}
              onClick={() => openProcessDialog(progress)}
              title="Process payout"
            >
              <EditIcon color="info" />
            </Button>
          )}
          <Button
            variant="text"
            size="large"
            sx={{ minWidth: "32px", p: 0 }}
            onClick={() => {
              setSelectedProgress(progress);
              setViewDialog(true);
            }}
            title="View details"
          >
            <VisibilityIcon color="primary" />
          </Button>
        </ArgonBox>
      ),
    };
  });
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <ArgonBox p={3}>
                <ArgonBox
                  display="flex"
                  alignItems={{ xs: "stretch", md: "flex-start" }}
                  justifyContent="space-between"
                  flexDirection={{ xs: "column", md: "row" }}
                  gap={2}
                  mb={2}
                >
                  <ArgonBox minWidth={0} flex={1}>
                    <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
                      SPIFF Progress
                    </ArgonTypography>

                    <ArgonBox sx={{ overflowX: "auto" }}>
                      <ApnaTabs
                        tabsData={tabsData}
                        activeTab={currentTab}
                        onTabChange={(tab) => {
                          setCurrentTab(tab);
                          setPagination((prev) => ({ ...prev, page: 1 }));
                        }}
                      />
                    </ArgonBox>
                  </ArgonBox>

                  <ArgonBox
                    sx={{
                      width: { xs: "100%", md: 300 },
                      flexShrink: 0,
                      pt: { xs: 0, md: 5.75 },
                      position: "relative",
                    }}
                  >
                    <ArgonTypography
                      variant="caption"
                      color="secondary"
                      display="block"
                      textAlign={{ xs: "left", md: "right" }}
                      sx={{
                        position: { xs: "static", md: "absolute" },
                        top: { md: 1},
                        right: { md: 0 },
                      }}
                    >
                      Earned: Rs {stats.earnedAmount || 0} | Paid: Rs {stats.paidAmount || 0}
                    </ArgonTypography>
                    <ArgonInput
                      placeholder="Search tutor..."
                      size="small"
                      value={searchQuery}
                      onChange={(event) => {
                        setSearchQuery(event.target.value);
                        setPagination((prev) => ({ ...prev, page: 1 }));
                      }}
                    />
                  </ArgonBox>
                </ArgonBox>
              </ArgonBox>

              <ArgonBox
                sx={{
                  "& .MuiTableRow-root:not(:last-child)": {
                    "& td": {
                      borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                        `${borderWidth[1]} solid ${borderColor}`,
                    },
                  },
                }}
              >
                {loading ? (
                  <ArgonBox textAlign="center" py={4}>
                    <CircularProgress color="primary" />
                    <ArgonTypography variant="body2" color="text" mt={1}>
                      Loading SPIFF data...
                    </ArgonTypography>
                  </ArgonBox>
                ) : rows.length ? (
                  <Table columns={columns} rows={rows} />
                ) : (
                  <ArgonBox textAlign="center" py={4}>
                    <ArgonTypography variant="h6" color="text">
                      No SPIFF records found
                    </ArgonTypography>
                  </ArgonBox>
                )}
              </ArgonBox>

              {pagination.total > 0 && !loading && (
                <ArgonBox p={2}>
                  <PaginationBar
                    currentPage={pagination.page}
                    totalPages={pagination.totalPages}
                    rowsPerPage={rowsPerPage}
                    totalItems={pagination.total}
                    onPageChange={(event, page) => setPagination((prev) => ({ ...prev, page }))}
                    onRowsPerPageChange={(event) => {
                      setRowsPerPage(event.target.value);
                      setPagination((prev) => ({ ...prev, page: 1 }));
                    }}
                  />
                </ArgonBox>
              )}
            </Card>
          </Grid>
        </Grid>

        <Dialog open={viewDialog} onClose={() => setViewDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <ArgonBox display="flex" alignItems="center" justifyContent="space-between">
              <ArgonTypography variant="h6">SPIFF Details</ArgonTypography>
              <IconButton onClick={() => setViewDialog(false)}>
                <CloseIcon />
              </IconButton>
            </ArgonBox>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <ArgonTypography variant="caption" color="secondary">
                  Tutor
                </ArgonTypography>
                <ArgonTypography variant="body2" fontWeight="medium">
                  {selectedProgress?.tutorId?.fullName || "N/A"}
                </ArgonTypography>
              </Grid>
              <Grid item xs={6}>
                <ArgonTypography variant="caption" color="secondary">
                  Progress
                </ArgonTypography>
                <ArgonTypography variant="body2" fontWeight="medium">
                  {selectedProgress?.approvalStreak || 0}/{selectedProgress?.requiredApprovals || 0}
                </ArgonTypography>
              </Grid>
              <Grid item xs={6}>
                <ArgonTypography variant="caption" color="secondary">
                  Earned Date
                </ArgonTypography>
                <ArgonTypography variant="body2" fontWeight="medium">
                  {selectedProgress?.earnedDate
                    ? new Date(selectedProgress.earnedDate).toLocaleDateString()
                    : "N/A"}
                </ArgonTypography>
              </Grid>
              <Grid item xs={6}>
                <ArgonTypography variant="caption" color="secondary">
                  Transaction ID
                </ArgonTypography>
                <ArgonTypography variant="body2" fontWeight="medium">
                  {selectedProgress?.transactionId || "N/A"}
                </ArgonTypography>
              </Grid>
              <Grid item xs={12}>
                <ArgonTypography variant="caption" color="secondary">
                  Counted Demos
                </ArgonTypography>
                {(selectedProgress?.approvedDemos || []).map((demo, index) => (
                  <ArgonTypography key={demo.demoId || index} variant="caption" display="block">
                    {index + 1}. {demo.demoId} -{" "}
                    {demo.approvedAt ? new Date(demo.approvedAt).toLocaleDateString() : "N/A"}
                  </ArgonTypography>
                ))}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <ArgonButton variant="outlined" color="primary" onClick={() => setViewDialog(false)}>
              Close
            </ArgonButton>
          </DialogActions>
        </Dialog>

        <Dialog open={processDialog} onClose={() => setProcessDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <ArgonBox display="flex" alignItems="center" justifyContent="space-between">
              <ArgonTypography variant="h6">Process SPIFF Payout</ArgonTypography>
              <IconButton onClick={() => setProcessDialog(false)}>
                <CloseIcon />
              </IconButton>
            </ArgonBox>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} mt={0.5}>
              <Grid item xs={12}>
                <ArgonTypography variant="caption" fontWeight="bold">
                  Approved / Declined
                </ArgonTypography>
                <select
                  value={processStatus}
                  onChange={(event) => setProcessStatus(event.target.value)}
                  style={{
                    width: "100%",
                    minHeight: "42px",
                    padding: "8px 12px",
                    borderRadius: "8px",
                    border: "1px solid #d2d6da",
                    backgroundColor: "#fff",
                  }}
                >
                  <option value="">Select Status</option>
                  <option value="APPROVED">Approved</option>
                  <option value="DECLINED">Declined</option>
                </select>
              </Grid>
              <Grid item xs={12}>
                <ArgonTypography variant="caption" fontWeight="bold">
                  Pay Amount
                </ArgonTypography>
                <ArgonInput
                  fullWidth
                  type="number"
                  value={payAmount}
                  onChange={(event) => setPayAmount(event.target.value)}
                />
              </Grid>
              {processStatus === "APPROVED" && (
                <>
                  <Grid item xs={12}>
                    <ArgonTypography variant="caption" fontWeight="bold">
                      Transaction ID
                    </ArgonTypography>
                    <ArgonInput
                      fullWidth
                      value={transactionId}
                      onChange={(event) => setTransactionId(event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ArgonTypography variant="caption" fontWeight="bold">
                      Payment Screenshot
                    </ArgonTypography>
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={(event) => setScreenShotFile(event.target.files?.[0] || null)}
                      style={{ width: "100%", marginTop: 8 }}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <ArgonButton
              variant="outlined"
              color="secondary"
              onClick={() => setProcessDialog(false)}
              disabled={processing}
            >
              Cancel
            </ArgonButton>
            <ArgonButton
              variant="contained"
              color="primary"
              onClick={handleProcessPayout}
              disabled={processing}
            >
              {processing ? <CircularProgress size={18} color="inherit" /> : "Process"}
            </ArgonButton>
          </DialogActions>
        </Dialog>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Spiff;











